import { useCallback, useContext, useEffect, useState } from "react";
import { Role, User } from "../../app/backendTypes";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  FormControl,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useRoleMembers } from "../../app/hooks";
import Loading from "../common/Loading";
import Error from "../common/Error";
import { Close, Delete, Person, PersonAdd } from "@mui/icons-material";
import UserPicker from "../common/users/UserPicker";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import { AxiosError } from "axios";
import UserSpan from "../common/users/UserSpan";
import Morselbar from "../common/Morselbar";

export default function Roles() {
  const [role, setRole] = useState<Role | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const paramRole = searchParams.get("role");

    if (paramRole !== null && paramRole in Role) setRole(paramRole as Role);
  }, [searchParams]);

  return (
    <>
      <RolePicker
        value={role}
        onChange={(val) => {
          setRole(val);

          if (val === null) setSearchParams({});
          else if (val !== role) setSearchParams({ role: val });
        }}
      />

      {role !== null && <RoleMembers role={role} />}
    </>
  );
}

function RoleMembers(props: { role: Role }) {
  const { role } = props;

  const { members, error, loading, reload } = useRoleMembers(role);

  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const [removeMember, setRemoveMember] = useState<User | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [message, setMessage] = useState<{
    error?: string;
    content: string;
  }>({ content: "" });

  let comp = <></>;
  if (loading) comp = <Loading />;
  else if (error) comp = <Error error={error} />;
  else
    comp = (
      <List dense>
        {members.map((member, i) => (
          <ListItem
            key={i}
            secondaryAction={
              <IconButton color="error" onClick={() => setRemoveMember(member)}>
                <Delete />
              </IconButton>
            }
          >
            <ListItemIcon>
              <Box
                component={Link}
                to={`/admin/users?id=${member.employee.id}`}
              >
                <Person color="secondary" />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={member.employee.name}
              secondary={member.employee.username}
            />
          </ListItem>
        ))}
      </List>
    );

  return (
    <>
      <Card sx={{ width: "100%", maxWidth: "sm" }}>
        <CardHeader
          title="Members"
          titleTypographyProps={{ color: "primary" }}
          action={
            <IconButton
              color="secondary"
              sx={{ marginRight: 2 }}
              size="large"
              onClick={() => setAddMemberOpen(true)}
            >
              <PersonAdd fontSize="large" />
            </IconButton>
          }
          subheader={matchRoleDescription(role)}
        />
        <CardContent>{comp}</CardContent>
      </Card>

      <AddMemberDialog
        open={addMemberOpen}
        setOpen={setAddMemberOpen}
        role={role}
        callback={reload}
        setAlertOpen={setAlertOpen}
        setMessage={setMessage}
      />

      <RemoveMemberDialog
        member={removeMember}
        setMember={setRemoveMember}
        role={role}
        callback={reload}
        setAlertOpen={setAlertOpen}
        setMessage={setMessage}
      />

      <Morselbar
        open={alertOpen}
        setOpen={setAlertOpen}
        content={message.content}
        error={message.error}
      />
    </>
  );
}

function RemoveMemberDialog(props: {
  member: User | null;
  setMember: (val: User | null) => void;
  role: Role;
  callback: () => void;
  setAlertOpen: (val: boolean) => void;
  setMessage: (val: { error?: string; content: string }) => void;
}) {
  const { member, setMember, role, callback, setAlertOpen, setMessage } = props;

  const [loading, setLoading] = useState(false);

  const client = useContext(HttpClientContext);

  const removeMember = useCallback(() => {
    setLoading(true);

    client
      ?.delete(`/api/roles/${role}/users/${member?.vc_id}`)
      .then(() => {
        setMessage({
          error: undefined,
          content: `${member?.employee.username} was removed from ${role} role`,
        });
        setAlertOpen(true);
        callback();
        setMember(null);
      })
      .catch((e: AxiosError) => {
        setMessage({
          error: e.response?.headers[X_REQUEST_ID],
          content: `Failed to remove ${member?.employee.username} from ${role} role`,
        });
        setAlertOpen(true);
      })
      .finally(() => setLoading(false));
  }, [callback, client, member, role, setAlertOpen, setMessage, setMember]);

  return (
    <>
      {member !== null && (
        <Dialog open>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography>
                This action will{" "}
                <Typography component="span" color="error" fontWeight="bold">
                  revoke
                </Typography>{" "}
                the role{" "}
                <Typography
                  component="span"
                  color="secondary"
                  fontWeight="bold"
                >
                  {role}
                </Typography>{" "}
                from <UserSpan user={member} />
              </Typography>
              <IconButton onClick={() => setMember(null)}>
                <Close />
              </IconButton>
            </Box>

            <br />
            <Button
              variant="outlined"
              disabled={loading}
              onClick={() => removeMember()}
              color="error"
            >
              Confirm
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function AddMemberDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  role: Role;
  callback: () => void;
  setAlertOpen: (val: boolean) => void;
  setMessage: (val: { error?: string; content: string }) => void;
}) {
  const { open, setOpen, role, callback, setAlertOpen, setMessage } = props;

  const [newMember, setNewMember] = useState<User | null>(null);

  const [loading, setLoading] = useState(false);

  const client = useContext(HttpClientContext);

  const addMember = useCallback(() => {
    if (newMember === null) return;

    setLoading(true);

    client
      ?.post(`/api/roles/${role}/users/${newMember.vc_id}`)
      .then(() => {
        setMessage({
          error: undefined,
          content: `${newMember.employee.username} was added to ${role} role`,
        });
        setAlertOpen(true);
        callback();
        setOpen(false);
      })
      .catch((e: AxiosError) => {
        setMessage({
          error: e.response?.headers[X_REQUEST_ID],
          content: `Failed to add ${newMember.employee.username} to ${role} role`,
        });
        setAlertOpen(true);
      })
      .finally(() => setLoading(false));
  }, [callback, client, newMember, role, setAlertOpen, setMessage, setOpen]);

  return (
    <Dialog open={open}>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography>
            This action will{" "}
            <Typography component="span" color="primary" fontWeight="bold">
              grant
            </Typography>{" "}
            the role{" "}
            <Typography component="span" color="secondary" fontWeight="bold">
              {role}
            </Typography>
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <br />
        <UserPicker
          initialValue={newMember?.employee.id || undefined}
          onChange={setNewMember}
          small
          vc_only
        />

        <br />
        <br />
        <Button
          variant="outlined"
          disabled={newMember === null || loading}
          onClick={() => addMember()}
        >
          Confirm
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function matchRoleDescription(role: Role) {
  let description = "We don't know what this role does";

  switch (role) {
    case Role.Administrator: {
      description =
        "Have the highest authority and can read sensitive information or credentials";
      break;
    }

    case Role.Developer: {
      description =
        "Are able to activate potentially destructive tools that should not be used under normal circumstances";
      break;
    }

    case Role.CatalogEditor: {
      description =
        "Can add new catalog items, edit existing ones, and delete them";
      break;
    }
      
    case Role.Security: {
      description =
        "Role for security team members, allowing them to manage security-related tasks and tools";
      break;
    }
  }

  return description;
}

function RolePicker(props: {
  value: Role | null;
  onChange: (value: Role | null) => void;
}) {
  const { value, onChange } = props;

  return (
    <FormControl fullWidth sx={{ maxWidth: "300px" }}>
      <FormGroup>
        <Autocomplete
          value={value}
          options={Object.keys(Role)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Pick a role" />
          )}
          onChange={(_, value) => onChange(value as Role)}
        />
      </FormGroup>
    </FormControl>
  );
}
